import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { baseURL } from '../services/api/apiConfigMain';

// Public release metadata the backend exposes at
// /api/public/app-releases — populated whenever an admin uploads a
// new APK via Settings → App Releases. `null` means no build has
// been published yet for that app.
interface PublicAppRelease {
  versionName: string;
  versionCode: number;
  fileSize: number;
  changelog: string | null;
  uploadedAt: string;
  downloadUrl: string;
}
interface AppReleaseMap {
  farmer: PublicAppRelease | null;
  delivery: PublicAppRelease | null;
}

const formatBytes = (n: number | undefined | null): string => {
  if (!n || n <= 0) return '';
  const mb = n / (1024 * 1024);
  return mb >= 100 ? `${mb.toFixed(0)} MB` : `${mb.toFixed(1)} MB`;
};

interface LandingSettings {
  hero: {
    title: string;
    subtitle: string;
    ctaText: string;
    ctaLink: string;
  };
  apps: {
    farmer: { name: string; playStoreUrl: string; appStoreUrl: string; enabled: boolean };
    delivery: { name: string; playStoreUrl: string; appStoreUrl: string; enabled: boolean };
  };
  features: Array<{ title: string; description: string; icon: string }>;
  footer: {
    companyName: string;
    email: string;
    phone: string;
  };
}

const DEFAULTS: LandingSettings = {
  hero: {
    title: 'Raitha Bazar',
    subtitle: 'Fresh fruits, vegetables and daily groceries — delivered to your doorstep',
    ctaText: 'Download the App',
    ctaLink: '#downloads',
  },
  apps: {
    farmer: { name: 'Raitha Bazar', playStoreUrl: '', appStoreUrl: '', enabled: true },
    delivery: { name: 'Raitha Bazar Delivery', playStoreUrl: '', appStoreUrl: '', enabled: true },
  },
  features: [
    { title: 'Fresh Fruits & Vegetables', description: 'Daily fresh produce sourced directly from farms — best quality at the best price', icon: 'leaf' },
    { title: 'Daily Essentials', description: 'Milk, atta, dal, spices — every household staple in one app', icon: 'shield' },
    { title: 'Fast Delivery', description: 'Place your order and get it delivered home in minutes — no queues, no waiting', icon: 'truck' },
    { title: 'Best Prices', description: 'Direct sourcing, no middlemen — prices lower than your local market', icon: 'tag' },
    { title: 'Quality Guarantee', description: '100% fresh, genuine products — full refund if you are not satisfied', icon: 'heart' },
    { title: 'Cash on Delivery', description: 'See your order first, pay later — COD available on every order', icon: 'bolt' },
  ],
  footer: { companyName: 'Raitha Bazar', email: 'support@citybasket.in', phone: '' },
};

/* ─── Icon Components ─── */
const FeatureIcon: React.FC<{ icon: string; className?: string }> = ({ icon, className = 'w-6 h-6' }) => {
  const props = { className, fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' };
  switch (icon) {
    case 'leaf':
      return <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>;
    case 'truck':
      return <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10l2-1 2 1 2-1 2 1zm6 0V9a1 1 0 00-1-1h-2l-3 4h4v4l2-1 2 1z" /><circle cx="6.5" cy="18.5" r="1.5" strokeWidth={2} fill="none" /><circle cx="17.5" cy="18.5" r="1.5" strokeWidth={2} fill="none" /></svg>;
    case 'tag':
      return <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>;
    case 'shield':
      return <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>;
    case 'heart':
      return <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>;
    case 'clock':
      return <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    default:
      return <svg {...props}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>;
  }
};

const PlayStoreIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" /></svg>
);

const AppStoreIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71,19.5C17.88,20.5 17,21.4 15.66,21.41C14.32,21.42 13.87,20.57 12.37,20.57C10.87,20.57 10.37,21.37 9.1,21.41C7.79,21.45 6.8,20.39 5.96,19.39C4.25,17.35 2.97,13.7 4.74,11.19C5.61,9.95 6.96,9.17 8.42,9.15C9.72,9.13 10.93,10.06 11.72,10.06C12.51,10.06 13.97,8.93 15.55,9.11C16.2,9.14 17.77,9.38 18.76,10.84C18.66,10.91 16.71,12.06 16.73,14.47C16.76,17.32 19.17,18.28 19.2,18.29L18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" /></svg>
);

// Direct-APK download glyph — used when an admin has published a
// build via Settings → App Releases but the app isn't on the Play
// Store yet. Distinguishable from the store icons (download arrow
// over a phone outline) so users don't mistake it for a redirect.
const ApkDownloadIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

/* ─── Main Component ─── */
const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn, user } = useAppSelector((state) => state.auth);
  const [settings, setSettings] = useState<LandingSettings>(DEFAULTS);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [releases, setReleases] = useState<AppReleaseMap>({ farmer: null, delivery: null });

  useEffect(() => {
    if (isLoggedIn && user?.role) {
      navigate(user.role === 'superadmin' ? '/superadmin' : '/admin');
    }
  }, [isLoggedIn, user?.role, navigate]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${baseURL}/app-settings/landing`);
        const json = await res.json();
        if (json.success && json.data?.settings) {
          setSettings((prev) => ({ ...prev, ...json.data.settings }));
        }
      } catch {
        // Use defaults
      }
    };
    fetchSettings();
  }, []);

  // Latest APK releases (farmer + delivery) — populated by the
  // backend's app-release upload flow. When a build exists, the
  // download buttons in each app card pick it up automatically.
  useEffect(() => {
    const fetchReleases = async () => {
      try {
        const res = await fetch(`${baseURL}/public/app-releases`);
        const json = await res.json();
        if (json?.success && json?.data) {
          setReleases({
            farmer: json.data.farmer ?? null,
            delivery: json.data.delivery ?? null,
          });
        }
      } catch {
        // No release info — buttons fall back to "Coming Soon".
      }
    };
    fetchReleases();
  }, []);

  const s = settings;

  return (
    <div className="min-h-screen bg-white font-sans antialiased text-gray-800">

      {/* ────────────── NAVBAR ────────────── */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-sm">
                <span className="text-white font-extrabold text-base">C</span>
              </div>
              <span className="text-xl font-extrabold bg-gradient-to-r from-emerald-700 to-green-600 bg-clip-text text-transparent">
                {s.hero.title}
              </span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-gray-600 hover:text-emerald-600 transition">Services</a>
              <a href="#how-it-works" className="text-sm font-medium text-gray-600 hover:text-emerald-600 transition">How It Works</a>
              <a href="#downloads" className="text-sm font-medium text-gray-600 hover:text-emerald-600 transition">Download App</a>
              <a href="#contact" className="text-sm font-medium text-gray-600 hover:text-emerald-600 transition">Contact</a>
              <Link to="/login" className="text-sm font-semibold bg-emerald-600 text-white px-5 py-2.5 rounded-xl hover:bg-emerald-700 transition shadow-sm hover:shadow-md">
                Admin Login
              </Link>
            </div>

            {/* Mobile menu button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-lg hover:bg-gray-100">
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                }
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-gray-100 mt-2 pt-3 space-y-2">
              <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-emerald-50 rounded-lg">Services</a>
              <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-emerald-50 rounded-lg">How It Works</a>
              <a href="#downloads" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-emerald-50 rounded-lg">Download App</a>
              <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-emerald-50 rounded-lg">Contact</a>
              <Link to="/login" className="block px-3 py-2 text-sm font-semibold text-emerald-600 hover:bg-emerald-50 rounded-lg">Admin Login</Link>
            </div>
          )}
        </div>
      </nav>

      {/* ────────────── HERO SECTION ────────────── */}
      <section className="relative pt-16 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-800 via-green-700 to-emerald-600" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-emerald-100">Trusted by 10,000+ Households</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.1] mb-6">
              {s.hero.title}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-emerald-100 mb-4 leading-relaxed max-w-2xl">
              {s.hero.subtitle}
            </p>
            <p className="text-base text-emerald-200/80 mb-10 max-w-xl">
              Your online grocery store — fruits, vegetables, milk, atta, spices and so much more, ordered from home and delivered fast.
            </p>

            <div className="flex flex-wrap gap-4">
              <a
                href={s.hero.ctaLink}
                className="inline-flex items-center gap-2 bg-white text-emerald-700 font-bold px-8 py-4 rounded-2xl shadow-xl hover:shadow-2xl hover:bg-emerald-50 transition-all text-base"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                {s.hero.ctaText}
              </a>
              <a
                href="#features"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white font-semibold px-8 py-4 rounded-2xl border border-white/20 hover:bg-white/20 transition-all text-base"
              >
                Learn More
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom wave */}
        <div className="relative">
          <svg className="w-full h-16 md:h-24" viewBox="0 0 1440 96" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M0 96L60 85.3C120 75 240 53 360 42.7C480 32 600 32 720 42.7C840 53 960 75 1080 80C1200 85 1320 75 1380 69.3L1440 64V96H1380C1320 96 1200 96 1080 96C960 96 840 96 720 96C600 96 480 96 360 96C240 96 120 96 60 96H0Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* ────────────── STATS BAR ────────────── */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {[
              { number: '5000+', label: 'Products Available' },
              { number: '10K+', label: 'Happy Customers' },
              { number: '200+', label: 'Brands' },
              { number: '24/7', label: 'Support' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-emerald-600 to-green-500 bg-clip-text text-transparent mb-1">
                  {stat.number}
                </div>
                <div className="text-sm font-semibold text-gray-700">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────── FEATURES / PRODUCTS ────────────── */}
      <section id="features" className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4">
              Our Services
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              Everything Your Home Needs
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base md:text-lg">
              From fresh fruits and vegetables to daily essentials, dairy to packaged foods — find everything in one place at the best quality and best price.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {s.features.map((feature, i) => (
              <div
                key={i}
                className="group bg-white rounded-2xl p-7 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-green-100 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <FeatureIcon icon={feature.icon} className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────── HOW IT WORKS ────────────── */}
      <section id="how-it-works" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4">
              How It Works
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              Order in Just 3 Easy Steps
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Download the app, pick your products and place your order — it's that simple.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {[
              {
                step: '01',
                title: 'Download the App',
                desc: 'Get the Raitha Bazar app from the Google Play Store — completely free.',
                color: 'from-emerald-500 to-green-500',
                bg: 'bg-emerald-50',
              },
              {
                step: '02',
                title: 'Fill Your Cart',
                desc: 'Fruits, vegetables, daily essentials — add whatever you need. Full product details and prices shown upfront.',
                color: 'from-blue-500 to-indigo-500',
                bg: 'bg-blue-50',
              },
              {
                step: '03',
                title: 'Order & Get It Home',
                desc: 'Confirm your order and receive fresh groceries at your doorstep. Pay online or on delivery — your choice.',
                color: 'from-amber-500 to-orange-500',
                bg: 'bg-amber-50',
              },
            ].map((item, i) => (
              <div key={i} className="relative text-center">
                {/* Connector line (desktop only) */}
                {i < 2 && (
                  <div className="hidden md:block absolute top-16 left-[60%] w-[80%] border-t-2 border-dashed border-gray-200 z-0" />
                )}
                <div className={`relative z-10 ${item.bg} rounded-3xl p-8 md:p-10`}>
                  <div className={`inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} items-center justify-center mb-5 shadow-lg`}>
                    <span className="text-white text-xl font-extrabold">{item.step}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────── WHY TRUST US ────────────── */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-emerald-800 via-green-700 to-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4">
              Why Trust Us?
            </h2>
            <p className="text-emerald-200 max-w-xl mx-auto">
              We lead on quality, price and service — every single order.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '100%', title: 'Genuine Products', desc: 'Only branded and certified products — never duplicates.' },
              { icon: 'COD', title: 'Cash on Delivery', desc: 'Inspect your order at the door, then pay — COD available on every order.' },
              { icon: 'Free', title: 'Free Delivery', desc: 'Free delivery to your doorstep on every qualifying order.' },
              { icon: '24/7', title: 'Customer Support', desc: 'Any issue? Our team is here around the clock to help.' },
            ].map((item, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/15 transition">
                <div className="text-2xl font-extrabold text-green-300 mb-3">{item.icon}</div>
                <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                <p className="text-emerald-200 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────── DOWNLOAD APPS ────────────── */}
      <section id="downloads" className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-green-100 text-green-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4">
              Download Now
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              Get the App
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Pick the app that fits your need and download it now — completely free.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Shopping App (legacy `farmer` key in app-settings JSON) */}
            {s.apps.farmer.enabled && (
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start gap-5 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{s.apps.farmer.name}</h3>
                    <p className="text-gray-500 text-sm mt-1">For Shoppers</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Browse fruits, vegetables, milk and every daily essential in one app — order in seconds and get fast delivery.
                </p>
                <ul className="space-y-2 mb-8">
                  {['Daily fresh produce at the best prices', 'Pay with COD or online — your choice', 'Track every order in real time', 'Easy returns and refunds'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Direct-APK download — landed when admin uploads
                      a build via Settings → App Releases. Sits in front
                      of the Play / App Store buttons so users on the
                      pre-Play-Store rollout see something actionable. */}
                  {releases.farmer && (
                    <a
                      href={releases.farmer.downloadUrl}
                      download
                      className="flex-1 inline-flex items-center justify-center gap-2.5 bg-emerald-600 text-white px-6 py-3.5 rounded-xl hover:bg-emerald-700 transition text-sm font-semibold shadow-sm"
                    >
                      <ApkDownloadIcon /> APK Download
                      <span className="text-xs font-normal opacity-80">
                        v{releases.farmer.versionName}{formatBytes(releases.farmer.fileSize) && ` · ${formatBytes(releases.farmer.fileSize)}`}
                      </span>
                    </a>
                  )}
                  {s.apps.farmer.playStoreUrl ? (
                    <a href={s.apps.farmer.playStoreUrl} target="_blank" rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-2.5 bg-gray-900 text-white px-6 py-3.5 rounded-xl hover:bg-gray-800 transition text-sm font-semibold shadow-sm">
                      <PlayStoreIcon /> Google Play
                    </a>
                  ) : null}
                  {s.apps.farmer.appStoreUrl ? (
                    <a href={s.apps.farmer.appStoreUrl} target="_blank" rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-2.5 bg-gray-900 text-white px-6 py-3.5 rounded-xl hover:bg-gray-800 transition text-sm font-semibold shadow-sm">
                      <AppStoreIcon /> App Store
                    </a>
                  ) : null}
                  {!releases.farmer && !s.apps.farmer.playStoreUrl && !s.apps.farmer.appStoreUrl && (
                    <div className="flex-1 text-center py-3 bg-gray-100 rounded-xl text-gray-400 font-medium text-sm">
                      Coming Soon
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Delivery App */}
            {s.apps.delivery.enabled && (
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start gap-5 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10l2-1 2 1 2-1 2 1zm6 0V9a1 1 0 00-1-1h-2l-3 4h4v4l2-1 2 1z" />
                      <circle cx="6.5" cy="18.5" r="1.5" strokeWidth={2} fill="none" /><circle cx="17.5" cy="18.5" r="1.5" strokeWidth={2} fill="none" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{s.apps.delivery.name}</h3>
                    <p className="text-gray-500 text-sm mt-1">For Delivery Partners</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Become a delivery partner — pick up orders, deliver them, and earn on every trip. Simple, reliable and built for riders.
                </p>
                <ul className="space-y-2 mb-8">
                  {['Easy order assignment', 'Built-in GPS navigation', 'Track your daily earnings', 'Flexible working hours'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                  {releases.delivery && (
                    <a
                      href={releases.delivery.downloadUrl}
                      download
                      className="flex-1 inline-flex items-center justify-center gap-2.5 bg-blue-600 text-white px-6 py-3.5 rounded-xl hover:bg-blue-700 transition text-sm font-semibold shadow-sm"
                    >
                      <ApkDownloadIcon /> APK Download
                      <span className="text-xs font-normal opacity-80">
                        v{releases.delivery.versionName}{formatBytes(releases.delivery.fileSize) && ` · ${formatBytes(releases.delivery.fileSize)}`}
                      </span>
                    </a>
                  )}
                  {s.apps.delivery.playStoreUrl ? (
                    <a href={s.apps.delivery.playStoreUrl} target="_blank" rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-2.5 bg-gray-900 text-white px-6 py-3.5 rounded-xl hover:bg-gray-800 transition text-sm font-semibold shadow-sm">
                      <PlayStoreIcon /> Google Play
                    </a>
                  ) : null}
                  {s.apps.delivery.appStoreUrl ? (
                    <a href={s.apps.delivery.appStoreUrl} target="_blank" rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-2.5 bg-gray-900 text-white px-6 py-3.5 rounded-xl hover:bg-gray-800 transition text-sm font-semibold shadow-sm">
                      <AppStoreIcon /> App Store
                    </a>
                  ) : null}
                  {!releases.delivery && !s.apps.delivery.playStoreUrl && !s.apps.delivery.appStoreUrl && (
                    <div className="flex-1 text-center py-3 bg-gray-100 rounded-xl text-gray-400 font-medium text-sm">
                      Coming Soon
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ────────────── CTA BANNER ────────────── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-emerald-600 to-green-700 rounded-3xl p-10 md:p-16 shadow-xl">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Start Shopping Today
            </h2>
            <p className="text-emerald-100 text-lg mb-8 max-w-xl mx-auto">
              Download the Raitha Bazar app and order fresh fruits, vegetables and daily groceries — straight from your phone, delivered to your door.
            </p>
            <a
              href={s.hero.ctaLink}
              className="inline-flex items-center gap-2 bg-white text-emerald-700 font-bold px-10 py-4 rounded-2xl shadow-xl hover:shadow-2xl hover:bg-emerald-50 transition-all text-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              {s.hero.ctaText}
            </a>
          </div>
        </div>
      </section>

      {/* ────────────── FOOTER ────────────── */}
      <footer id="contact" className="bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                  <span className="text-white font-extrabold text-base">C</span>
                </div>
                <span className="text-xl font-extrabold text-white">{s.footer.companyName}</span>
              </div>
              <p className="text-sm leading-relaxed max-w-md mb-6">
                A complete grocery commerce platform for your home — fruits, vegetables, dairy and daily essentials at the best quality and prices, delivered straight to your door.
              </p>
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-gray-800 hover:bg-emerald-600 flex items-center justify-center transition cursor-pointer">
                  <svg className="w-4 h-4 text-gray-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                </div>
                <div className="w-9 h-9 rounded-lg bg-gray-800 hover:bg-emerald-600 flex items-center justify-center transition cursor-pointer">
                  <svg className="w-4 h-4 text-gray-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
                </div>
                <div className="w-9 h-9 rounded-lg bg-gray-800 hover:bg-emerald-600 flex items-center justify-center transition cursor-pointer">
                  <svg className="w-4 h-4 text-gray-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" /></svg>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Links</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#features" className="hover:text-emerald-400 transition">Services</a></li>
                <li><a href="#how-it-works" className="hover:text-emerald-400 transition">How It Works</a></li>
                <li><a href="#downloads" className="hover:text-emerald-400 transition">Download App</a></li>
                <li><Link to="/login" className="hover:text-emerald-400 transition">Admin Panel</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Contact</h4>
              <ul className="space-y-3 text-sm">
                {s.footer.email && (
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    <a href={`mailto:${s.footer.email}`} className="hover:text-emerald-400 transition">{s.footer.email}</a>
                  </li>
                )}
                {s.footer.phone && (
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                    <a href={`tel:${s.footer.phone}`} className="hover:text-emerald-400 transition">{s.footer.phone}</a>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-gray-500">
              &copy; {new Date().getFullYear()} {s.footer.companyName}. All rights reserved.
            </p>
            <p className="text-xs text-gray-600">
              Made with care for households across India
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
